// src/admin/AnnouncementEdit.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';

const AnnouncementEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    status: 'draft',
    is_featured: false
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [existingImage, setExistingImage] = useState('');

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (!id) return;
    fetch(`https://fellfell.ir/api/announcements.php?action=single&id=${id}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          const item = data.data;
          setFormData({
            title: item.title || '',
            summary: item.summary || '',
            content: item.content || '',
            status: item.status || 'draft',
            is_featured: item.is_featured === 1
          });
          setExistingImage(item.image || '');
        } else {
          showToast('خطا در دریافت اطلاعات', 'error');
        }
        setLoading(false);
      })
      .catch(() => {
        showToast('خطا در ارتباط با سرور', 'error');
        setLoading(false);
      });
  }, [id]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('image', file);
    const res = await fetch('https://fellfell.ir/backend/api/upload-announcement.php', {
      method: 'POST',
      credentials: 'include',
      body: fd
    });
    const data = await res.json();
    if (data.success) return data.filename;
    throw new Error(data.message || 'خطا در آپلود');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      showToast('عنوان و محتوا الزامی است', 'error');
      return;
    }
    setSaving(true);
    try {
      let imageName = existingImage;
      if (imageFile) {
        imageName = await uploadImage(imageFile);
      }
      const payload = { ...formData, image: imageName };
      const res = await fetch(`https://fellfell.ir/api/announcements.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        showToast('اطلاعیه با موفقیت ویرایش شد', 'success');
        setTimeout(() => navigate('/admin/announcements'), 1500);
      } else {
        showToast('خطا: ' + data.message, 'error');
      }
    } catch (err: any) {
      showToast('خطا: ' + err.message, 'error');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>⏳ در حال بارگذاری...</div>;

  return (
    <div style={{ direction: 'rtl', maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      {toast && <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', padding: '14px 28px', borderRadius: '12px', zIndex: 9999, background: toast.type === 'success' ? '#2e7d32' : '#c62828', color: '#fff', boxShadow: '0 6px 25px rgba(0,0,0,0.25)', animation: 'slideDown 0.3s ease-out' }}>{toast.message}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#1e3a1e' }}>✏️ ویرایش اطلاعیه</h1>
        <Link to="/admin/announcements" style={{ color: '#2e7d32', textDecoration: 'none' }}>← بازگشت</Link>
      </div>
      <form onSubmit={handleSubmit} style={{ background: '#fff', padding: '30px', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600' }}>عنوان <span style={{ color: '#d32f2f' }}>*</span></label>
          <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '2px solid #ddd' }} required />
        </div>
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600' }}>خلاصه</label>
          <input type="text" value={formData.summary} onChange={e => setFormData({ ...formData, summary: e.target.value })} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '2px solid #ddd' }} />
        </div>
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600' }}>محتوا <span style={{ color: '#d32f2f' }}>*</span></label>
          <textarea value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })} rows={8} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '2px solid #ddd', resize: 'vertical' }} required />
        </div>
        <div style={{ display: 'flex', gap: '15px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600' }}>وضعیت</label>
            <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '2px solid #ddd' }}>
              <option value="draft">پیش‌نویس</option>
              <option value="published">منتشر شده</option>
            </select>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <input type="checkbox" checked={formData.is_featured} onChange={e => setFormData({ ...formData, is_featured: e.target.checked })} />
              <span>⭐ ویژه (نمایش در بنر)</span>
            </label>
          </div>
        </div>
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600' }}>تصویر</label>
          <div style={{ border: '2px dashed #ddd', borderRadius: '10px', padding: '20px', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }} onClick={() => document.getElementById('image-input')?.click()}>
            <input type="file" id="image-input" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
            {(imagePreview || existingImage) ? (
              <img src={imagePreview || `/uploads/announcements/${existingImage}`} alt="پیش‌نمایش" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
            ) : (
              <div><span style={{ fontSize: '40px' }}>🖼️</span><p style={{ color: '#888' }}>برای آپلود کلیک کنید</p></div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <Link to="/admin/announcements" style={{ padding: '12px 24px', background: '#e0e0e0', color: '#333', borderRadius: '10px', textDecoration: 'none' }}>انصراف</Link>
          <button type="submit" disabled={saving} style={{ padding: '12px 30px', background: '#2e7d32', color: '#fff', borderRadius: '10px', border: 'none', fontSize: '15px', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? '⏳ ذخیره...' : '💾 ذخیره تغییرات'}</button>
        </div>
      </form>
    </div>
  );
};

export default AnnouncementEdit;